/**
 * Auth Context
 *
 * Provides authentication state and region detection for the app.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 13 - Integrate region detection into AuthContext
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { detectAndStoreRegion, getStoredRegion } from '../services/regionDetection.js';
import { DatabaseService } from '../utils/databaseService.js';
import { trackConversion } from '../utils/gtmHelpers';
import logger from '../utils/logger';
import { persistAdvertisementConsent, recordSignupAttribution } from '../utils/posthogAttribution';
import { hashEmail } from '../utils/privacy.js';
import { initializeSession, resetSession } from '../utils/sessionTrackingHelper';
import supabase from '../utils/supabaseClient';
import {
  getBasicLifecycleProperties,
  getUserLifecycleProperties,
} from '../utils/userLifecycleHelper';

// Create the context with a default shape
export const AuthContext = createContext({
  user: null,
  session: null,
  isLoading: true,
  hasCompletedOnboarding: null,
  hasActiveFeed: null,
  hasSelectedCars: null,
  hasFitScores: null,
  hasPostcode: null,
  onboardingIntent: null,
  region: 'EU',
  countryCode: null,
  logout: async () => {},
});

// Helper to detect public routes (defined outside component to avoid recreating on each render)
const isPublicRoute = () => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  const publicRoutes = ['/', '/how-it-works', '/privacy-policy', '/terms-and-conditions'];
  return publicRoutes.includes(pathname);
};

// Create the provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  // Initialize isLoading based on route type - false for public routes, true for protected
  const [isLoading, setIsLoading] = useState(!isPublicRoute());
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
  const [hasActiveFeed, setHasActiveFeed] = useState(null);
  const [hasSelectedCars, setHasSelectedCars] = useState(null);
  const [hasFitScores, setHasFitScores] = useState(null);
  const [hasPostcode, setHasPostcode] = useState(null);
  const [onboardingIntent, setOnboardingIntent] = useState(null);
  const [region, setRegion] = useState('EU'); // Default to EU
  const [countryCode, setCountryCode] = useState(null);
  const [isRegionDetected, setIsRegionDetected] = useState(false); // Track if region detection has completed
  const posthog = usePostHog();
  const _router = useRouter();
  const identifiedUserRef = useRef(null);
  const processedOnboardingRef = useRef(new Set()); // Track processed sessions to prevent duplicates
  const trackedConversionsRef = useRef(new Set()); // Track conversion events to prevent duplicates
  const regionDetectedRef = useRef(false); // Track if region detection has run
  const regionRef = useRef(region); // Stable ref for use inside auth effect (avoids re-running effect on region change)
  const countryCodeRef = useRef(countryCode);
  // Latest session, read inside stable callbacks (e.g. checkOnboardingStatus)
  // so they don't need `session` in their dependency arrays. The long-lived
  // auth effect captures checkOnboardingStatus once; reading via this ref keeps
  // its metadata fallback current instead of frozen at the mount-time session.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Check if user is a new signup based on account creation time
  const isNewSignup = (user) => {
    if (!user?.created_at) return false;
    const accountAge = Date.now() - new Date(user.created_at).getTime();
    // Consider new if account was created within the last 2 minutes
    return accountAge < 120000;
  };

  // Check onboarding completion status
  const checkOnboardingStatus = useCallback(async (userId) => {
    try {
      logger.log('[AuthProvider] Checking onboarding status for user:', userId);
      const isComplete = await DatabaseService.hasCompletedOnboarding(userId);
      setHasCompletedOnboarding(isComplete);
      logger.log('[AuthProvider] Onboarding complete:', isComplete);

      // RACE CONDITION FALLBACK: If database trigger was slow
      // Check user_metadata for answers and manually save them
      if (!isComplete && sessionRef.current?.user?.user_metadata?.onboarding_answers) {
        const answers = sessionRef.current.user.user_metadata.onboarding_answers;
        const hasAnswers = answers && Object.keys(answers).length > 0;

        if (hasAnswers) {
          logger.log('[AuthProvider] Found answers in user_metadata - triggering manual save (database trigger may have been slow)');

          try {
            const { error } = await DatabaseService.saveOnboardingAnswers(userId, answers);

            if (!error) {
              logger.log('[AuthProvider] Manual save successful - updating onboarding status to complete');
              setHasCompletedOnboarding(true);
            } else {
              logger.error('[AuthProvider] Manual save failed:', error);
            }
          } catch (saveError) {
            logger.error('[AuthProvider] Exception during manual save:', saveError);
          }
        }
      }
    } catch (error) {
      logger.error('[AuthProvider] Failed to check onboarding status after retries:', {
        userId,
        errorMessage: error.message,
        errorCode: error.code,
      });

      // Default to true to avoid blocking users on persistent errors
      // The error has already been logged to Sentry by DatabaseService with full context
      setHasCompletedOnboarding(true);
      logger.log(
        '[AuthProvider] Defaulting onboarding status to true to avoid blocking user experience'
      );
    }
  }, []);

  // Extracted analytics handling function (non-blocking)
  const handleUserAnalytics = async (session) => {
    if (!session?.user) return;

    logger.log('[AuthProvider] Processing analytics for user:', session.user.id);

    // Check for pending onboarding answers (from OAuth flow)
    try {
      const sessionKey = session.user.id;
      const answersJson = await AsyncStorage.getItem('onboardingAnswers');
      logger.log('[AuthProvider] Onboarding answers check:', !!answersJson);

      if (answersJson && !processedOnboardingRef.current.has(sessionKey)) {
        // Additional safeguard: Check user's current recommendation status before proceeding
        // This prevents duplicate job creation if user already has recommendations
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('recommendation_status')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          logger.error('[AuthProvider] Failed to check user status:', userError);
          // Don't proceed if we can't verify status
          return;
        }

        // Only proceed if user is in a state that needs onboarding processing
        const safeStatuses = ['pending_onboarding_completion', 'pending_email_confirmation'];
        // Statuses where handle_new_user trigger already saved answers from signup metadata
        const triggerProcessedStatuses = ['pending_generation', 'pending_car_selection', 'processing'];
        if (!safeStatuses.includes(userData?.recommendation_status)) {
          if (triggerProcessedStatuses.includes(userData?.recommendation_status)) {
            // Answers were already saved by handle_new_user trigger — just clean up AsyncStorage
            logger.log(
              `[AuthProvider] User ${session.user.id} status is '${userData?.recommendation_status}'. ` +
                'Answers already saved by trigger, cleaning up AsyncStorage.'
            );
          } else {
            logger.log(
              `[AuthProvider] User ${session.user.id} status is '${userData?.recommendation_status}'. ` +
                'Skipping onboarding save (already processed or in progress).'
            );
            // Existing user tried to re-onboard — flag for welcome-back notice
            if (userData?.recommendation_status) {
              await AsyncStorage.setItem('returningUserNotice', 'true');
            }
          }
          // Clean up stale AsyncStorage data
          await AsyncStorage.removeItem('onboardingAnswers');
          return;
        }

        processedOnboardingRef.current.add(sessionKey);
        const answers = JSON.parse(answersJson);
        const storedIntent = await AsyncStorage.getItem('onboardingIntent');
        const { error } = await DatabaseService.saveOnboardingAnswers(session.user.id, answers, storedIntent);

        // ALWAYS clean AsyncStorage, even on error, to prevent retry loops
        await AsyncStorage.removeItem('onboardingAnswers');
        await AsyncStorage.removeItem('onboardingIntent');

        if (error) {
          logger.error('[AuthProvider] Failed to save onboarding answers:', error);
          logger.log('[AuthProvider] AsyncStorage cleaned to prevent duplicate attempts');
          processedOnboardingRef.current.delete(sessionKey);
        } else {
          logger.log('[AuthProvider] Onboarding answers saved successfully');
        }
      }
    } catch (error) {
      logger.error('[AuthProvider] Error processing onboarding answers:', error);
    }

    // Handle PostHog analytics (only once per user)
    if (identifiedUserRef.current !== session.user.id) {
      try {
        logger.log('[AuthProvider] Initializing PostHog for user');
        initializeSession(false);

        const lifecycleProps = await getUserLifecycleProperties(session.user.id, supabase);
        const basicProps = getBasicLifecycleProperties(session.user);

        posthog?.identify(session.user.id, {
          // Raw email/name intentionally omitted so PostHog only receives the
          // Supabase user id + lifecycle/region properties, matching the
          // §5.3 disclosure. Email/name remain in Supabase auth and are
          // joined to PostHog distinct_id downstream if BI ever needs them.
          // `name` is set to the Supabase user id (non-PII) so the PostHog UI
          // displays it instead of a random anonymous distinct_id, making
          // persons matchable against Supabase at a glance.
          name: session.user.id,
          created_at: session.user.created_at,
          email_verified: !!session.user.email_confirmed_at,
          region: regionRef.current,
          country_code: countryCodeRef.current,
          region_detection_status: 'detected',
          ...basicProps,
          ...lifecycleProps,
        });

        logger.log('[AuthProvider] PostHog user identified');
      } catch (error) {
        logger.error('[AuthProvider] PostHog setup error:', error);
        // Fallback to basic identification
        try {
          posthog?.identify(session.user.id, {
            // No raw email/name (matches §5.3 disclosure). `name` = Supabase
            // user id for dashboard display, see primary identify above.
            name: session.user.id,
            ...getBasicLifecycleProperties(session.user),
          });
        } catch (fallbackError) {
          logger.error('[AuthProvider] PostHog fallback failed:', fallbackError);
        }
      }

      // Set Sentry context
      try {
        logger.log('[AuthProvider] Setting Sentry context');
        const emailHash = await hashEmail(session.user.email);
        Sentry.setUser({
          id: session.user.id,
          email: emailHash || undefined,
        });

        const posthogSessionId = posthog?.getSessionId?.();
        if (posthogSessionId) {
          Sentry.setTag('posthog_session_id', posthogSessionId);
        }

        logger.log('[AuthProvider] Sentry context set');
      } catch (error) {
        logger.error('[AuthProvider] Sentry setup error:', error);
        // Still set user ID even if email hashing fails
        try {
          Sentry.setUser({ id: session.user.id });
        } catch (sentryError) {
          logger.error('[AuthProvider] Sentry fallback failed:', sentryError);
        }
      }

      identifiedUserRef.current = session.user.id;
    }

    logger.log('[AuthProvider] Analytics processing complete');
  };

  // Detect region on app initialization (once, before auth check)
  useEffect(() => {
    if (regionDetectedRef.current) return;

    const detectRegion = async () => {
      regionDetectedRef.current = true;

      try {
        logger.log('[AuthProvider] Starting region detection');

        // First check if region is already stored
        const stored = await getStoredRegion();
        if (stored.region && stored.country_code) {
          logger.log('[AuthProvider] Using stored region:', stored.region);
          setRegion(stored.region);
          setCountryCode(stored.country_code);
          setIsRegionDetected(true);
          return;
        }

        // If region is stored but country_code is missing, a previous detection
        // failed and cached a fallback — re-detect to get an accurate result
        if (stored.region && !stored.country_code) {
          logger.log('[AuthProvider] Stored region has no country_code (previous failure), re-detecting...');
        }

        // Otherwise, detect and store
        const result = await detectAndStoreRegion();
        logger.log('[AuthProvider] Region detected:', result.region);
        setRegion(result.region);
        setCountryCode(result.country_code);
        setIsRegionDetected(true);
      } catch (error) {
        logger.error('[AuthProvider] Region detection failed:', error);
        // Keep default EU region on error
        setIsRegionDetected(true); // Still mark as detected (using fallback)
      }
    };

    detectRegion();
  }, []);

  // Keep refs in sync with state (no effect re-run, just assignment)
  useEffect(() => {
    regionRef.current = region;
    countryCodeRef.current = countryCode;
  }, [region, countryCode]);

  useEffect(() => {
    logger.log('[AuthProvider] Initializing auth state');

    // Check initial session immediately on mount
    const checkInitialSession = async () => {
      try {
        logger.log('[AuthProvider] Checking initial session');
        const { data: { session }, error } = await supabase.auth.getSession();
        logger.log('[AuthProvider] Initial session check complete, hasSession:', !!session);

        if (error) {
          logger.error('[AuthProvider] Error getting initial session:', error);
          setSession(null);
          setUser(null);
          setIsLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Check onboarding status if user is signed in
        if (session?.user) {
          // Save any pending onboarding answers FIRST (DOOCARS-12 fix)
          // Only this part must be sequential — it writes data that checkOnboardingStatus reads
          try {
            const answersJson = await AsyncStorage.getItem('onboardingAnswers');
            if (answersJson && !processedOnboardingRef.current.has(session.user.id)) {
              const { data: userData, error: userError } = await supabase
                .from('users')
                .select('recommendation_status')
                .eq('id', session.user.id)
                .single();

              const safeStatuses = ['pending_onboarding_completion', 'pending_email_confirmation'];
              // Statuses where handle_new_user trigger already saved answers from signup metadata
              const triggerProcessedStatuses = ['pending_generation', 'pending_car_selection', 'processing'];
              if (!userError && safeStatuses.includes(userData?.recommendation_status)) {
                processedOnboardingRef.current.add(session.user.id);
                const answers = JSON.parse(answersJson);
                const storedIntent = await AsyncStorage.getItem('onboardingIntent');
                const { error } = await DatabaseService.saveOnboardingAnswers(session.user.id, answers, storedIntent);
                await AsyncStorage.removeItem('onboardingAnswers');
                await AsyncStorage.removeItem('onboardingIntent');
                if (error) {
                  logger.error('[AuthProvider] Failed to save onboarding answers:', error);
                  processedOnboardingRef.current.delete(session.user.id);
                } else {
                  logger.log('[AuthProvider] Onboarding answers saved successfully');
                  if (storedIntent) setOnboardingIntent(storedIntent);
                }
              } else {
                await AsyncStorage.removeItem('onboardingAnswers');
                await AsyncStorage.removeItem('onboardingIntent');
                // Only flag returning user if we confirmed the user exists with a non-safe status
                // AND the status wasn't already set by the handle_new_user trigger (new signup).
                // If userError is truthy (e.g. row not found for a brand-new OAuth user), skip the notice.
                if (!userError && userData?.recommendation_status &&
                    !triggerProcessedStatuses.includes(userData?.recommendation_status)) {
                  logger.log('[AuthProvider] Existing user re-onboarded via OAuth, setting returningUser flag');
                  await AsyncStorage.setItem('returningUserNotice', 'true');
                } else if (!userError && triggerProcessedStatuses.includes(userData?.recommendation_status)) {
                  logger.log('[AuthProvider] Answers already saved by trigger, cleaning up AsyncStorage');
                }
              }
            }
          } catch (error) {
            logger.error('[AuthProvider] Error saving pending onboarding answers:', error);
          }

          // Now check routing-critical states in parallel (all are independent reads)
          await Promise.all([
            checkOnboardingStatus(session.user.id),
            supabase
              .from('user_listing_feeds')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .eq('status', 'active')
              .limit(1)
              .then(({ count, error: feedError }) => {
                setHasActiveFeed(feedError ? false : count > 0);
              })
              .catch((err) => {
                logger.error('[AuthProvider] Active feed check failed:', err);
                setHasActiveFeed(false);
              }),
            supabase
              .from('user_model_fit_scores')
              .select('user_model_fit_score_id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .eq('selected_for_feed', true)
              .limit(1)
              .then(({ count, error: selectedErr }) => {
                setHasSelectedCars(selectedErr ? false : count > 0);
              })
              .catch((err) => {
                logger.error('[AuthProvider] Selected cars check failed:', err);
                setHasSelectedCars(false);
              }),
            supabase
              .from('user_model_fit_scores')
              .select('user_model_fit_score_id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .is('deleted_at', null)
              .limit(1)
              .then(({ count, error: fitErr }) => {
                setHasFitScores(fitErr ? false : count > 0);
              })
              .catch((err) => {
                logger.error('[AuthProvider] Fit scores check failed:', err);
                setHasFitScores(false);
              }),
            supabase
              .from('onboarding_answers')
              .select('answers->q22_location_postal_code')
              .eq('user_id', session.user.id)
              .maybeSingle()
              .then(({ data, error: pcErr }) => {
                const pc = !pcErr && data?.q22_location_postal_code;
                setHasPostcode(!!pc && pc !== '');
              })
              .catch((err) => {
                logger.error('[AuthProvider] Postcode check failed:', err);
                setHasPostcode(false);
              }),
          ]);

          // Non-blocking: analytics (PostHog, Sentry)
          handleUserAnalytics(session).catch(error => {
            logger.error('[AuthProvider] Analytics error:', error);
          });

          // Blocking: load account region + intent before setIsLoading(false)
          // This ensures the user's stored region (not IP-detected) is used
          // for queries like car_makes_models, even when traveling abroad
          try {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('region, country_code, onboarding_intent')
              .eq('id', session.user.id)
              .single();
            if (!userError && userData?.region) {
              setRegion(userData.region);
              setCountryCode(userData.country_code);
            }
            // Always resolve onboardingIntent so the routing guard unblocks.
            // 'none' = DB query ran but user has no intent (pre-v5 users).
            // null = not loaded yet (used by the guard to wait).
            setOnboardingIntent(!userError ? (userData?.onboarding_intent || 'none') : 'none');
          } catch (err) {
            logger.error('[AuthProvider] Failed to load user region:', err);
            setOnboardingIntent('none');
          }
        }

        setIsLoading(false);
      } catch (error) {
        logger.error('[AuthProvider] Failed to check initial session:', error);
        setSession(null);
        setUser(null);
        setIsLoading(false);
      }
    };

    // Non-blocking initialization for public routes
    const publicRoute = isPublicRoute();
    if (publicRoute) {
      logger.log('[AuthProvider] Public route detected - non-blocking auth check');
      // isLoading already set to false in initial state, just run auth in background
      checkInitialSession(); // Run auth check in background (non-blocking)
    } else {
      logger.log('[AuthProvider] Protected route detected - blocking auth check');
      checkInitialSession(); // Block until auth completes (will set isLoading=false when done)
    }

    // Safety timeout: if isLoading is still true after 10s, force-resolve to prevent stuck screens
    const loadingTimeout = setTimeout(() => {
      setIsLoading((current) => {
        if (current) {
          logger.error('[AuthProvider] Loading timeout reached (10s) — force-resolving to prevent stuck screen');
          Sentry.captureMessage('Auth loading timeout reached', { level: 'warning' });
          // Default onboarding to true so user isn't blocked
          setHasCompletedOnboarding((prev) => prev === null ? true : prev);
          setHasActiveFeed((prev) => prev === null ? false : prev);
          setHasSelectedCars((prev) => prev === null ? false : prev);
          setHasFitScores((prev) => prev === null ? false : prev);
          setHasPostcode((prev) => prev === null ? false : prev);
          return false;
        }
        return current;
      });
    }, 10000);

    // Periodic session validity check (every 5 minutes)
    const sessionCheckInterval = setInterval(
      async () => {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();

        if (error || !currentSession) {
          logger.log(
            '[AuthProvider] Session check: Session invalid or expired, clearing auth state'
          );
          setSession(null);
          setUser(null);
        }
      },
      5 * 60 * 1000
    ); // Check every 5 minutes

    // Listen for auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      logger.log('[AuthProvider] Auth state change:', _event, 'hasSession:', !!session);

      // Handle sign out
      if (_event === 'SIGNED_OUT') {
        logger.log('[AuthProvider] User signed out, clearing state');
        setSession(null);
        setUser(null);
        setHasCompletedOnboarding(null);
        setHasActiveFeed(null);
        setHasSelectedCars(null);
        setHasFitScores(null);
        setHasPostcode(null);
        setOnboardingIntent(null);
        posthog?.reset();
        resetSession();
        identifiedUserRef.current = null;
        processedOnboardingRef.current.clear();
        trackedConversionsRef.current.clear();
        Sentry.setUser(null);
        Sentry.setTag('posthog_session_id', null);
        // Note: Navigation is handled by _layout.tsx to avoid race conditions
        return;
      }

      // Update session state
      setSession(session);
      setUser(session?.user ?? null);

      // Resolve routing states for any event that provides a session.
      // IMPORTANT: Deferred to next tick via setTimeout to prevent deadlock.
      // During _initialize(), supabase-js's _notifyAllSubscribers() awaits this
      // callback. Any supabase.from() call here triggers auth.getSession() which
      // awaits initializePromise — but initializePromise can't resolve until
      // _notifyAllSubscribers finishes → circular wait / deadlock.
      if (session?.user && _event !== 'SIGNED_OUT') {
        // Clear OAuth hash fragment from URL after Supabase has processed it.
        // Without this, the #access_token=... persists and leaks tokens into
        // analytics events and can cause re-processing on navigation.
        if (typeof window !== 'undefined' && window.location.hash.includes('access_token=')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          logger.log('[AuthProvider] Cleared OAuth hash fragment from URL');
        }

        setTimeout(() => {
          (async () => {
            try {
        // SIGNED_IN-only: PostHog identify + conversion tracking
        if (_event === 'SIGNED_IN') {
        // Identify user in PostHog for experiment assignment continuity
        // This triggers $identify events that allow the webhook to merge anonymous → authenticated assignments
        // CRITICAL: Wrapped in try-catch to prevent blocking checkOnboardingStatus if PostHog fails
        if (posthog && !identifiedUserRef.current) {
          try {
            // If the distinct_id changed during OAuth redirect (cookie loss),
            // the pre-OAuth anonymous events (onboarding) are orphaned.
            // Create an alias BEFORE identify so PostHog links the old anonymous ID
            // to the current distinct_id, then identify merges everything into user.id.
            const savedDistinctId = await AsyncStorage.getItem('posthog_pre_oauth_distinct_id');
            if (savedDistinctId && savedDistinctId !== posthog.get_distinct_id?.()) {
              posthog.alias(savedDistinctId);
              logger.log('[AuthProvider] PostHog alias created for pre-OAuth distinct_id:', savedDistinctId);
            }
            await AsyncStorage.removeItem('posthog_pre_oauth_distinct_id');

            // Identify user; merges current anonymous distinct_id with user.id.
            // Raw email intentionally omitted per §5.3 disclosure. `name` =
            // Supabase user id for dashboard display (non-PII).
            posthog.identify(session.user.id, {
              name: session.user.id,
              created_at: session.user.created_at,
              region: regionRef.current,
              country_code: countryCodeRef.current,
              region_detection_status: 'detected',
            });
            identifiedUserRef.current = session.user.id;
            logger.log('[AuthProvider] User identified in PostHog:', session.user.id);
          } catch (error) {
            logger.error('[AuthProvider] PostHog identify failed (non-blocking):', error);
            // Continue execution - this is non-critical for login flow
          }
        }

        // Step 7b: persist signup-time acquisition attribution (write-once,
        // consent-gated) on the first authenticated session. Fire-and-forget +
        // idempotent (DB COALESCE); covers OAuth (AsyncStorage survives the
        // redirect, like posthog_pre_oauth_distinct_id above) and email alike.
        // Never throws into this handler (fully guarded internally).
        recordSignupAttribution(posthog);

        // Mirror the user's CookieYes advertisement-consent answer onto their
        // row on each authenticated session. Backfills returning users over
        // time so we can build a lawful Google Customer Match audience
        // (privacy policy 5.18). Fire-and-forget; no-op when no session.
        persistAdvertisementConsent();

        // Determine auth method from user metadata
        const authMethod = session.user.app_metadata?.provider || 'unknown';
        const isNewUser = isNewSignup(session.user);

        // Track conversion for new signups with enhanced conversion data
        if (isNewUser) {
          const signupConversionKey = `${session.user.id}_signup`;
          if (!trackedConversionsRef.current.has(signupConversionKey)) {
            logger.log('[AuthProvider] New signup detected, tracking conversion with email');

            // Track PostHog + GTM conversions for OAuth signups only
            // Email signups already fire signup_success + signup_complete in EmailSignupScreen/AuthScreen
            if (authMethod !== 'email') {
              try {
                posthog?.capture('signup_success', {
                  signup_type: 'oauth',
                  requires_confirmation: false,
                  provider: authMethod,
                });
              } catch (error) {
                logger.error('[AuthProvider] PostHog capture failed (non-blocking):', error);
              }

              trackConversion(
                'signup_complete',
                {
                  user_id: session.user.id,
                  signup_method: authMethod,
                  requires_confirmation: false,
                },
                { email: session.user.email }
              );
            }

            trackedConversionsRef.current.add(signupConversionKey);
            logger.log('[AuthProvider] Conversion tracked for new signup via', authMethod);
          }

          // FIX: Update region for OAuth signups
          // OAuth doesn't store options.data in user metadata like email signup does
          // So we need to update the public.users table with the stored region after OAuth completes
          if (authMethod !== 'email') {
            try {
              const { region: storedRegion, country_code: storedCountryCode } = await getStoredRegion();
              logger.log('[AuthProvider] Updating OAuth user region:', storedRegion, storedCountryCode);

              if (storedRegion) {
                const { error: updateError } = await supabase
                  .from('users')
                  .update({
                    region: storedRegion,
                    country_code: storedCountryCode
                  })
                  .eq('id', session.user.id);

                if (updateError) {
                  logger.error('[AuthProvider] Failed to update OAuth user region:', updateError);
                } else {
                  logger.log('[AuthProvider] Successfully updated OAuth user region to:', storedRegion);
                  // Update context state
                  setRegion(storedRegion);
                  setCountryCode(storedCountryCode);
                }
              }
            } catch (regionError) {
              logger.error('[AuthProvider] Error updating OAuth user region:', regionError);
            }
          }

          // Apply email digest opt-in preference from AsyncStorage (set during signup flow)
          try {
            const digestOptIn = await AsyncStorage.getItem('emailDigestOptIn');
            if (digestOptIn === 'true') {
              const { error: digestError } = await supabase
                .from('users')
                .update({ email_digest_opted_in: true })
                .eq('id', session.user.id);

              if (digestError) {
                logger.error('[AuthProvider] Failed to set email_digest_opted_in:', digestError);
              } else {
                logger.log('[AuthProvider] Email digest opt-in set for new user');
                trackConversion(
                  'email_alerts_subscribed',
                  { user_id: session.user.id, source: 'signup' },
                  { email: session.user.email }
                );
              }
            }
            // Clean up regardless of result
            await AsyncStorage.removeItem('emailDigestOptIn');
          } catch (digestErr) {
            logger.error('[AuthProvider] Error handling email digest opt-in:', digestErr);
          }
        } else {
          // This is a returning user login - track OAuth logins with enhanced conversions
          // Email logins are already tracked in AuthScreen with email from form
          const loginConversionKey = `${session.user.id}_login_${Date.now()}`;

          // Only track OAuth logins here (email logins tracked in AuthScreen)
          if (authMethod !== 'email' && !trackedConversionsRef.current.has(loginConversionKey)) {
            logger.log('[AuthProvider] OAuth login detected, tracking conversion with email');

            // Track conversion with user email for enhanced conversions
            trackConversion(
              'login_success',
              {
                user_id: session.user.id,
                login_method: 'oauth',
                provider: authMethod,
              },
              { email: session.user.email }
            );

            trackedConversionsRef.current.add(loginConversionKey);
            logger.log('[AuthProvider] Conversion tracked for OAuth login via', authMethod);
          }
        }
        } // end SIGNED_IN-only block

        // --- Routing checks: run for ANY event with a session ---
        // Save pending onboarding answers FIRST (DOOCARS-12 fix)
        try {
          const answersJson = await AsyncStorage.getItem('onboardingAnswers');
          if (answersJson && !processedOnboardingRef.current.has(session.user.id)) {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('recommendation_status')
              .eq('id', session.user.id)
              .single();

            const safeStatuses = ['pending_onboarding_completion', 'pending_email_confirmation'];
            // Statuses where handle_new_user trigger already saved answers from signup metadata
            const triggerProcessedStatuses = ['pending_generation', 'pending_car_selection', 'processing'];
            if (!userError && safeStatuses.includes(userData?.recommendation_status)) {
              processedOnboardingRef.current.add(session.user.id);
              const answers = JSON.parse(answersJson);
              const storedIntent = await AsyncStorage.getItem('onboardingIntent');
              const { error } = await DatabaseService.saveOnboardingAnswers(session.user.id, answers, storedIntent);
              await AsyncStorage.removeItem('onboardingAnswers');
              await AsyncStorage.removeItem('onboardingIntent');
              if (error) {
                logger.error('[AuthProvider] Failed to save onboarding answers:', error);
                processedOnboardingRef.current.delete(session.user.id);
              } else {
                logger.log('[AuthProvider] Onboarding answers saved');
                if (storedIntent) setOnboardingIntent(storedIntent);
              }
            } else {
              await AsyncStorage.removeItem('onboardingAnswers');
              await AsyncStorage.removeItem('onboardingIntent');
              // Only flag returning user if status wasn't set by handle_new_user trigger (new signup)
              if (!userError && userData?.recommendation_status &&
                  !triggerProcessedStatuses.includes(userData?.recommendation_status)) {
                await AsyncStorage.setItem('returningUserNotice', 'true');
              } else if (!userError && triggerProcessedStatuses.includes(userData?.recommendation_status)) {
                logger.log('[AuthProvider] Answers already saved by trigger, cleaning up AsyncStorage');
              }
            }
          }
        } catch (error) {
          logger.error('[AuthProvider] Error saving pending onboarding answers:', error);
        }

        // Check routing-critical states in parallel
        try {
          await Promise.all([
            checkOnboardingStatus(session.user.id),
            supabase
              .from('user_listing_feeds')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .eq('status', 'active')
              .limit(1)
              .then(({ count, error: feedError }) => {
                const hasFeed = feedError ? false : count > 0;
                setHasActiveFeed(hasFeed);
              }),
            supabase
              .from('user_model_fit_scores')
              .select('user_model_fit_score_id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .eq('selected_for_feed', true)
              .limit(1)
              .then(({ count, error: selectedErr }) => {
                const hasCars = selectedErr ? false : count > 0;
                setHasSelectedCars(hasCars);
              }),
            supabase
              .from('user_model_fit_scores')
              .select('user_model_fit_score_id', { count: 'exact', head: true })
              .eq('user_id', session.user.id)
              .is('deleted_at', null)
              .limit(1)
              .then(({ count, error: fitErr }) => {
                setHasFitScores(fitErr ? false : count > 0);
              }),
            supabase
              .from('onboarding_answers')
              .select('answers->q22_location_postal_code')
              .eq('user_id', session.user.id)
              .maybeSingle()
              .then(({ data, error: pcErr }) => {
                const pc = !pcErr && data?.q22_location_postal_code;
                setHasPostcode(!!pc && pc !== '');
              }),
          ]);
        } catch (error) {
          logger.error('[AuthProvider] Error checking routing states:', error);
          setHasActiveFeed(false);
          setHasSelectedCars(false);
          setHasFitScores(false);
          setHasPostcode(false);
        }

        // Load account region + intent from DB (overrides IP-detected region)
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('region, country_code, onboarding_intent')
            .eq('id', session.user.id)
            .single();
          if (!userError && userData?.region) {
            setRegion(userData.region);
            setCountryCode(userData.country_code);
          }
          // Always resolve onboardingIntent so the routing guard unblocks.
          // 'none' = DB query ran but user has no intent (pre-v5 users).
          // null = not loaded yet (used by the guard to wait).
          setOnboardingIntent(!userError ? (userData?.onboarding_intent || 'none') : 'none');
        } catch (err) {
          logger.error('[AuthProvider] Failed to load user region:', err);
          setOnboardingIntent('none');
        }

        setIsLoading(false);

        // Non-blocking: analytics (PostHog, Sentry)
        handleUserAnalytics(session).catch(error => {
          logger.error('[AuthProvider] Analytics error:', error);
        });
            } catch (err) {
              logger.error('[AuthProvider] Error in deferred auth handler:', err);
              setIsLoading(false);
            }
          })();
        }, 0);
      }
    });

    // Cleanup subscription, interval, and timeout on unmount
    return () => {
      logger.log('[AuthProvider] Cleaning up auth listener');
      subscription?.unsubscribe();
      clearInterval(sessionCheckInterval);
      clearTimeout(loadingTimeout);
    };
  }, [posthog]); // region/countryCode accessed via refs to avoid effect re-runs

  // Define the logout function
  const logout = useCallback(async () => {
    logger.log('[AuthContext] logout() called - starting sign out process');
    posthog?.capture('logout');
    await supabase.auth.signOut();
    logger.log('[AuthContext] supabase.auth.signOut() completed');
    // The onAuthStateChange listener will handle clearing state and redirecting to landing page
  }, [posthog]);

  // Re-query routing-critical states from the database.
  // Call after user actions that change routing state (postcode save, car selection, etc.)
  const refreshRoutingState = useCallback(async () => {
    if (!user?.id) return;
    logger.log('[AuthProvider] Refreshing routing state for user:', user.id);
    try {
      await Promise.all([
        supabase
          .from('user_listing_feeds')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'active')
          .limit(1)
          .then(({ count, error: feedError }) => {
            setHasActiveFeed(feedError ? false : count > 0);
          }),
        supabase
          .from('user_model_fit_scores')
          .select('user_model_fit_score_id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('selected_for_feed', true)
          .limit(1)
          .then(({ count, error: selectedErr }) => {
            setHasSelectedCars(selectedErr ? false : count > 0);
          }),
        supabase
          .from('user_model_fit_scores')
          .select('user_model_fit_score_id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .limit(1)
          .then(({ count, error: fitErr }) => {
            setHasFitScores(fitErr ? false : count > 0);
          }),
        supabase
          .from('onboarding_answers')
          .select('answers->q22_location_postal_code')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data, error: pcErr }) => {
            const pc = !pcErr && data?.q22_location_postal_code;
            setHasPostcode(!!pc && pc !== '');
          }),
      ]);
    } catch (error) {
      logger.error('[AuthProvider] Error refreshing routing state:', error);
    }
  }, [user?.id]);

  const value = useMemo(() => ({
    user,
    session,
    isLoading,
    hasCompletedOnboarding,
    hasActiveFeed,
    hasSelectedCars,
    hasFitScores,
    hasPostcode,
    onboardingIntent,
    region,
    countryCode,
    isRegionDetected,
    logout,
    checkOnboardingStatus,
    refreshRoutingState,
  }), [
    user, session, isLoading, hasCompletedOnboarding, hasActiveFeed,
    hasSelectedCars, hasFitScores, hasPostcode, onboardingIntent, region,
    countryCode, isRegionDetected, logout, checkOnboardingStatus, refreshRoutingState,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create the hook that components will use to access the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
